import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  X,
  CheckCircle,
  User,
  Shield,
  FileCheck,
  Briefcase,
  Activity,
  Settings,
  Store,
  Download,
} from 'lucide-react';
import { ROUTES } from '../../../constants/routes.constants';
import { ROLE_LABELS, PERMISSIONS } from '../../../utils/rbac';
import { exportUserDetailsToPdf } from '../../../utils/exportUserDetailsPdf';
import Can from '../../auth/Can';

const EMPTY = 'Non renseigné';

const display = (value) => {
  if (value === null || value === undefined) return EMPTY;
  if (typeof value === 'string' && value.trim() === '') return EMPTY;
  if (typeof value === 'boolean') return value ? 'Oui' : 'Non';
  return value;
};

const formatDate = (dateString) => {
  if (!dateString) return EMPTY;
  return new Date(dateString).toLocaleDateString('fr-FR', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const getAbsoluteUrl = (relativePath) => {
  if (!relativePath || relativePath.startsWith('http')) return relativePath;
  return `${ROUTES.API_BASE_URL.replace('/api', '')}${relativePath}`;
};

const KYC_STATUS_LABELS = {
  NONE: 'Aucun',
  PENDING: 'En attente',
  VERIFIED: 'Validé',
  REJECTED: 'Rejeté',
};

const VENDOR_STATUS_LABELS = {
  PENDING: 'En attente',
  APPROVED: 'Approuvé',
  REJECTED: 'Rejeté',
};

const HOST_STATUS_LABELS = {
  '0': 'Non hôte',
  '1': 'Hôte actif',
  '2': 'Demande en attente',
};

const LOGIN_TYPE_LABELS = {
  email: 'E-mail',
  google: 'Google',
  apple: 'Apple',
};

const NOTIF_LABEL = (v) => (v === '1' || v === 1 || v === true ? 'Activé' : 'Désactivé');

const Field = ({ label, children, className = '' }) => (
  <div className={className}>
    <dt className="text-xs font-medium text-gray-500 mb-0.5">{label}</dt>
    <dd className="text-sm text-gray-900 font-medium break-words">{children}</dd>
  </div>
);

const ContactVerifyIcon = ({ field, isVerified, onVerify, disabled }) => {
  const label = field === 'email' ? 'E-mail' : 'Téléphone';
  if (isVerified) {
    return (
      <CheckCircle
        className="w-4 h-4 text-emerald-600 flex-shrink-0"
        title={`${label} vérifié`}
        aria-label={`${label} vérifié`}
      />
    );
  }
  return (
    <button
      type="button"
      onClick={() => onVerify(field)}
      disabled={disabled}
      title={`Marquer le ${label.toLowerCase()} comme vérifié`}
      className="p-0.5 rounded-full border border-gray-300 hover:border-emerald-500 hover:bg-emerald-50 disabled:opacity-50"
      aria-label={`Vérifier le ${label.toLowerCase()}`}
    >
      <CheckCircle className="w-4 h-4 text-gray-400" />
    </button>
  );
};

const KycImageBlock = ({ label, url }) => {
  if (!url) return null;
  const abs = getAbsoluteUrl(url);
  return (
    <div>
      <p className="text-xs text-gray-500 mb-2">{label}</p>
      <img
        src={abs}
        alt={label}
        className="w-full h-44 object-contain border border-gray-200 rounded-lg cursor-pointer hover:opacity-90"
        onClick={() => window.open(abs, '_blank', 'noopener,noreferrer')}
        onError={(e) => {
          e.target.src = 'https://via.placeholder.com/400x300?text=Image+indisponible';
        }}
      />
    </div>
  );
};

const BookingCard = ({ booking, role }) => (
  <div className="bg-gray-50 p-4 rounded-lg border border-gray-100">
    <div className="flex justify-between gap-3 items-start">
      <div className="min-w-0">
        <p className="font-medium text-gray-900 truncate">
          {booking.vehicle?._id?.title || booking.vehicle?.title || 'Véhicule'}
        </p>
        <p className="text-xs text-gray-500">
          {[booking.vehicle?._id?.specs?.brand?.name, booking.vehicle?._id?.specs?.model?.name]
            .filter(Boolean)
            .join(' ') || EMPTY}
        </p>
        <p className="text-xs text-gray-500 mt-1">
          {formatDate(booking.dates?.start)} → {formatDate(booking.dates?.end)}
        </p>
        {role === 'client' && booking.vendor && (
          <p className="text-xs text-gray-600 mt-1">
            Vendeur :{' '}
            {booking.vendor.vendorDetails?.companyName ||
              booking.vendor.fullName ||
              booking.vendor.email ||
              EMPTY}
          </p>
        )}
        {role === 'vendor' && booking.client && (
          <p className="text-xs text-gray-600 mt-1">
            Client : {booking.client.fullName || booking.client.email || EMPTY}
          </p>
        )}
      </div>
      <div className="text-right flex-shrink-0">
        <p className="font-semibold text-slate-900">
          {booking.financials?.total ?? 0} {booking.financials?.currency || 'MAD'}
        </p>
        <span
          className={`inline-block mt-1 px-2 py-0.5 rounded-full text-xs font-medium ${
            booking.status === 'COMPLETED'
              ? 'bg-emerald-100 text-emerald-800'
              : booking.status === 'CANCELLED'
                ? 'bg-red-100 text-red-800'
                : 'bg-amber-100 text-amber-800'
          }`}
        >
          {booking.status}
        </span>
      </div>
    </div>
  </div>
);

const TAB_DEFS = [
  { id: 'profile', label: 'Profil', icon: User },
  { id: 'account', label: 'Compte & RBAC', icon: Shield },
  { id: 'kyc', label: 'KYC', icon: FileCheck },
  { id: 'client', label: 'Profil client', icon: Briefcase },
  { id: 'activity', label: 'Activité', icon: Activity },
  { id: 'vendor', label: 'Agence', icon: Store, vendorOnly: true },
  { id: 'prefs', label: 'Préférences', icon: Settings },
];

/**
 * Modale admin : détail exhaustif d'un utilisateur (user + bookings + stats API).
 */
const UserDetailsModal = ({
  open,
  onClose,
  detailsData,
  listUser,
  isLoading,
  isProcessing,
  onVerifyContact,
  onVerifyKYC,
  onRejectKYC,
}) => {
  const [activeTab, setActiveTab] = useState('profile');
  const [isExportingPdf, setIsExportingPdf] = useState(false);

  const user = detailsData?.user || listUser;
  const isVendor = user?.role === 'vendor';

  const visibleTabs = useMemo(
    () => TAB_DEFS.filter((t) => !t.vendorOnly || isVendor),
    [isVendor]
  );

  if (!open || !user) return null;

  const kyc = user.kycDocuments;
  const ud = user.userDetails;
  const vd = user.vendorDetails;
  const birthdateDisplay =
    user.birthdate != null
      ? formatDate(user.birthdate)
      : ud?.birthdate
        ? display(ud.birthdate)
        : EMPTY;

  const permissions = user.effectivePermissions || user.permissions || [];

  const renderProfile = () => (
    <div className="grid sm:grid-cols-2 gap-4">
      <Field label="ID">{display(user._id)}</Field>
      <Field label="Nom complet">
        {display(
          user.fullName ||
            [user.firstName, user.middle, user.lastName].filter(Boolean).join(' ')
        )}
      </Field>
      <Field label="Prénom">{display(user.firstName)}</Field>
      <Field label="Nom">{display(user.lastName)}</Field>
      <Field label="Deuxième prénom">{display(user.middle)}</Field>
      <Field label="E-mail">
        <span className="inline-flex items-center gap-2 flex-wrap">
          {display(user.email)}
          <ContactVerifyIcon
            field="email"
            isVerified={Boolean(user.emailVerified)}
            onVerify={onVerifyContact}
            disabled={isProcessing}
          />
        </span>
      </Field>
      <Field label="Date de validation e-mail">
        {formatDate(user.emailVerifiedAt || user.email_verified_at)}
      </Field>
      <Field label="Téléphone">
        <span className="inline-flex items-center gap-2 flex-wrap">
          {user.phoneNumber
            ? `${user.countryCode || '+212'} ${user.phoneNumber}`
            : EMPTY}
          {user.phoneNumber ? (
            <ContactVerifyIcon
              field="phone"
              isVerified={Boolean(user.phoneVerified)}
              onVerify={onVerifyContact}
              disabled={isProcessing}
            />
          ) : null}
        </span>
      </Field>
      <Field label="Date de validation téléphone">
        {formatDate(user.phoneVerifiedAt || user.phone_verified_at)}
      </Field>
      <Field label="Date de naissance">{birthdateDisplay}</Field>
      <Field label="Photo de profil">
        {user.profilePicture ? (
          <img
            src={getAbsoluteUrl(user.profilePicture)}
            alt="Profil"
            className="h-16 w-16 rounded-full object-cover border"
          />
        ) : (
          EMPTY
        )}
      </Field>
      <Field label="Inscription">{formatDate(user.createdAt)}</Field>
      <Field label="Dernière mise à jour">{formatDate(user.updatedAt)}</Field>
      <Field label="IP d'inscription">{display(user.registration_ip)}</Field>
      <Field label="Dernière IP de connexion">{display(user.last_login_ip)}</Field>
    </div>
  );

  const renderAccount = () => (
    <div className="space-y-6">
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Rôle">{ROLE_LABELS[user.role] || display(user.role)}</Field>
        <Field label="Statut du compte">
          {user.accountStatus === 'SUSPENDED' ? 'Suspendu' : 'Actif'}
        </Field>
        <Field label="KYC (statut)">
          {KYC_STATUS_LABELS[user.kycStatus] || display(user.kycStatus)}
        </Field>
        <Field label="KYC vérifié (flag)">{display(user.kycVerified)}</Field>
        <Field label="CGU acceptées">{display(user.termsAccepted)}</Field>
        <Field label="Date d'acceptation des CGU">
          {formatDate(user.termsAcceptedAt || user.terms_accepted_at)}
        </Field>
        <Field label="Version token (sécurité)">{display(user.tokenVersion)}</Field>
      </div>
      {user.accountStatus === 'SUSPENDED' && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 space-y-2">
          <p className="text-sm font-semibold text-red-900">Suspension</p>
          <Field label="Date">{formatDate(user.suspendedAt)}</Field>
          <Field label="Motif">{display(user.suspendedReason)}</Field>
          <Field label="Suspendu par">
            {user.suspendedBy && typeof user.suspendedBy === 'object'
              ? display(user.suspendedBy.fullName || user.suspendedBy.email)
              : display(user.suspendedBy)}
          </Field>
        </div>
      )}
      <div>
        <p className="text-sm font-semibold text-gray-900 mb-2">Permissions effectives</p>
        {permissions.length === 0 ? (
          <p className="text-sm text-gray-500">{EMPTY}</p>
        ) : (
          <ul className="flex flex-wrap gap-1.5">
            {permissions.map((p) => (
              <li
                key={p}
                className="text-xs bg-slate-100 text-slate-700 px-2 py-1 rounded-md font-mono"
              >
                {p}
              </li>
            ))}
          </ul>
        )}
      </div>
      <p className="text-xs text-gray-500">
        <Link
          to={`${ROUTES.ADMIN_AUDIT_LOGS}?userId=${user._id}`}
          className="text-[#27489E] hover:underline"
        >
          Voir l&apos;historique d&apos;audit de cet utilisateur
        </Link>
      </p>
    </div>
  );

  const renderKyc = () => (
    <div className="space-y-6">
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Statut">{KYC_STATUS_LABELS[user.kycStatus] || display(user.kycStatus)}</Field>
        <Field label="Soumis le">{formatDate(kyc?.submittedAt)}</Field>
        <Field label="Vérifié le">{formatDate(kyc?.verifiedAt)}</Field>
        <Field label="Réf. permis principal">{display(kyc?.primaryReferenceNumber)}</Field>
        <Field label="Réf. permis secondaire">{display(kyc?.secondaryReferenceNumber)}</Field>
      </div>
      {user.identityImage && (
        <div>
          <p className="text-sm font-semibold mb-2">Pièce d&apos;identité</p>
          <KycImageBlock label="Identité" url={user.identityImage} />
        </div>
      )}
      {(kyc?.primaryDriverLicenseFront || kyc?.primaryDriverLicenseBack) && (
        <div>
          <p className="text-sm font-semibold mb-3">Permis principal</p>
          <div className="grid sm:grid-cols-2 gap-4">
            <KycImageBlock label="Recto" url={kyc?.primaryDriverLicenseFront} />
            <KycImageBlock label="Verso" url={kyc?.primaryDriverLicenseBack} />
          </div>
        </div>
      )}
      {(kyc?.secondaryDriverLicenseFront || kyc?.secondaryDriverLicenseBack) && (
        <div>
          <p className="text-sm font-semibold mb-3">Permis secondaire</p>
          <div className="grid sm:grid-cols-2 gap-4">
            <KycImageBlock label="Recto" url={kyc?.secondaryDriverLicenseFront} />
            <KycImageBlock label="Verso" url={kyc?.secondaryDriverLicenseBack} />
          </div>
        </div>
      )}
      {!kyc &&
        !user.identityImage &&
        user.kycStatus === 'NONE' && (
          <p className="text-sm text-gray-500">Aucun document KYC enregistré.</p>
        )}
    </div>
  );

  const renderClientProfile = () => (
    <div className="space-y-6">
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Permis (texte)">{display(ud?.driverLicense)}</Field>
        <Field label="Pays">{display(ud?.country)}</Field>
        <Field label="Pays par défaut (ISO)">{display(ud?.defaultCountry)}</Field>
        <Field label="Langue">{display(ud?.langauge)}</Field>
        <Field label="Portefeuille">{display(ud?.wallet)}</Field>
      </div>
      {ud?.intro ? (
        <Field label="Présentation">
          <span className="font-normal whitespace-pre-wrap">{ud.intro}</span>
        </Field>
      ) : null}
      <div>
        <p className="text-sm font-semibold text-gray-900 mb-2">Favoris (wishlist)</p>
        {!user.wishlist?.length ? (
          <p className="text-sm text-gray-500">{EMPTY}</p>
        ) : (
          <ul className="text-sm space-y-1">
            {user.wishlist.map((v) => (
              <li key={v._id || v} className="text-gray-700">
                {typeof v === 'object'
                  ? display(v.title || v.licencePlateNumber || v._id)
                  : display(v)}
              </li>
            ))}
          </ul>
        )}
      </div>
      {user.rating?.count > 0 && (
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Note moyenne">{user.rating.average?.toFixed(2)}</Field>
          <Field label="Nombre d&apos;avis">{user.rating.count}</Field>
        </div>
      )}
    </div>
  );

  const renderActivity = () => (
    <div className="space-y-6">
      {detailsData?.stats && (
        <div>
          <p className="text-sm font-semibold text-gray-900 mb-3">Statistiques client</p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="bg-gray-50 p-3 rounded-lg">
              <p className="text-xs text-gray-500">Réservations</p>
              <p className="text-xl font-bold text-slate-900">{detailsData.stats.totalBookings}</p>
            </div>
            <div className="bg-gray-50 p-3 rounded-lg">
              <p className="text-xs text-gray-500">Total dépensé</p>
              <p className="text-xl font-bold text-emerald-700">
                {Number(detailsData.stats.totalSpent || 0).toFixed(2)} MAD
              </p>
            </div>
            <div className="bg-gray-50 p-3 rounded-lg">
              <p className="text-xs text-gray-500">Complétées</p>
              <p className="text-xl font-bold text-blue-700">
                {detailsData.stats.completedBookings}
              </p>
            </div>
            <div className="bg-gray-50 p-3 rounded-lg">
              <p className="text-xs text-gray-500">Annulées</p>
              <p className="text-xl font-bold text-red-700">
                {detailsData.stats.cancelledBookings}
              </p>
            </div>
          </div>
        </div>
      )}
      <div>
        <p className="text-sm font-semibold text-gray-900 mb-3">
          Dernières réservations (client, max. 10)
        </p>
        {!detailsData?.bookings?.length ? (
          <p className="text-sm text-gray-500">{EMPTY}</p>
        ) : (
          <div className="space-y-2">
            {detailsData.bookings.map((b) => (
              <BookingCard key={b._id} booking={b} role="client" />
            ))}
          </div>
        )}
      </div>
    </div>
  );

  const renderVendor = () => {
    const vs = detailsData?.vendorStats;
    return (
      <div className="space-y-6">
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Statut agence">
            {VENDOR_STATUS_LABELS[user.vendorStatus] || display(user.vendorStatus)}
          </Field>
          <Field label="Statut hôte (app)">
            {HOST_STATUS_LABELS[user.hostStatus] || display(user.hostStatus)}
          </Field>
          <Field label="Raison sociale">{display(vd?.companyName)}</Field>
          <Field label="Identifiant fiscal">{display(vd?.taxId)}</Field>
          <Field label="Forme juridique">{display(vd?.legalForm)}</Field>
          <Field label="Adresse">{display(vd?.address)}</Field>
        </div>
        {vd?.logo && (
          <Field label="Logo">
            <img
              src={getAbsoluteUrl(vd.logo)}
              alt="Logo agence"
              className="h-14 object-contain"
            />
          </Field>
        )}
        {vs && (
          <div>
            <p className="text-sm font-semibold text-gray-900 mb-3">Statistiques vendeur</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              <div className="bg-gray-50 p-3 rounded-lg">
                <p className="text-xs text-gray-500">Véhicules</p>
                <p className="text-xl font-bold">{vs.vehicleCount}</p>
              </div>
              <div className="bg-gray-50 p-3 rounded-lg">
                <p className="text-xs text-gray-500">Réservations</p>
                <p className="text-xl font-bold">{vs.totalBookings}</p>
              </div>
              <div className="bg-gray-50 p-3 rounded-lg">
                <p className="text-xs text-gray-500">Revenu net</p>
                <p className="text-xl font-bold text-emerald-700">
                  {Number(vs.totalRevenue || 0).toFixed(2)} MAD
                </p>
              </div>
              <div className="bg-gray-50 p-3 rounded-lg">
                <p className="text-xs text-gray-500">En attente</p>
                <p className="text-xl font-bold">{vs.pendingBookings}</p>
              </div>
              <div className="bg-gray-50 p-3 rounded-lg">
                <p className="text-xs text-gray-500">Complétées</p>
                <p className="text-xl font-bold">{vs.completedBookings}</p>
              </div>
              <div className="bg-gray-50 p-3 rounded-lg">
                <p className="text-xs text-gray-500">Annulées</p>
                <p className="text-xl font-bold">{vs.cancelledBookings}</p>
              </div>
            </div>
          </div>
        )}
        <div>
          <p className="text-sm font-semibold text-gray-900 mb-3">
            Dernières réservations (vendeur, max. 10)
          </p>
          {!detailsData?.vendorBookings?.length ? (
            <p className="text-sm text-gray-500">{EMPTY}</p>
          ) : (
            <div className="space-y-2">
              {detailsData.vendorBookings.map((b) => (
                <BookingCard key={b._id} booking={b} role="vendor" />
              ))}
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderPrefs = () => (
    <div className="grid sm:grid-cols-2 gap-4">
      <Field label="Type de connexion">
        {LOGIN_TYPE_LABELS[user.loginType] || display(user.loginType)}
      </Field>
      <Field label="Forfait (packageId)">{display(user.packageId)}</Field>
      <Field label="Notifications SMS">{NOTIF_LABEL(user.smsNotification)}</Field>
      <Field label="Notifications e-mail">{NOTIF_LABEL(user.emailNotification)}</Field>
      <Field label="Notifications push">{NOTIF_LABEL(user.pushNotification)}</Field>
      <Field label="Statut hôte">{HOST_STATUS_LABELS[user.hostStatus] || display(user.hostStatus)}</Field>
    </div>
  );

  const tabContent = {
    profile: renderProfile,
    account: renderAccount,
    kyc: renderKyc,
    client: renderClientProfile,
    activity: renderActivity,
    vendor: renderVendor,
    prefs: renderPrefs,
  };

  const titleName = user.fullName || user.email || 'Utilisateur';

  const handleExportPdf = async () => {
    if (!detailsData?.user) {
      alert('Les données complètes ne sont pas encore chargées.');
      return;
    }
    setIsExportingPdf(true);
    try {
      exportUserDetailsToPdf(detailsData);
    } catch (err) {
      console.error(err);
      alert(err.message || 'Impossible de générer le PDF.');
    } finally {
      setIsExportingPdf(false);
    }
  };

  const exportDisabled = isLoading || isExportingPdf || !detailsData?.user;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} aria-hidden="true" />
      <div
        className="relative bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col"
        role="dialog"
        aria-labelledby="user-details-title"
      >
        <div className="flex items-center justify-between px-6 py-4 border-b bg-slate-900 text-white rounded-t-xl gap-2">
          <h2 id="user-details-title" className="text-lg font-semibold truncate flex-1 min-w-0">
            Détails — {titleName}
          </h2>
          <div className="flex items-center gap-1 shrink-0">
            <button
              type="button"
              onClick={handleExportPdf}
              disabled={exportDisabled}
              title="Télécharger le rapport PDF"
              className="p-2 rounded-lg hover:bg-white/10 disabled:opacity-40 disabled:cursor-not-allowed"
              aria-label="Télécharger le rapport PDF"
            >
              <Download size={20} />
            </button>
            <button
              type="button"
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-white/10"
              aria-label="Fermer"
            >
              <X size={22} />
            </button>
          </div>
        </div>

        <div className="flex border-b overflow-x-auto shrink-0">
          {visibleTabs.map((tab) => {
            const Icon = tab.icon;
            const active = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-1.5 px-4 py-2.5 text-xs sm:text-sm font-medium whitespace-nowrap border-b-2 transition ${
                  active
                    ? 'border-slate-900 text-slate-900'
                    : 'border-transparent text-gray-500 hover:text-gray-800'
                }`}
              >
                <Icon size={16} />
                {tab.label}
              </button>
            );
          })}
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {isLoading ? (
            <div className="flex items-center justify-center py-16 text-gray-500">
              <div className="animate-spin h-8 w-8 border-2 border-slate-900 border-t-transparent rounded-full" />
              <span className="ml-3">Chargement…</span>
            </div>
          ) : (
            tabContent[activeTab]?.()
          )}
        </div>

        <div className="flex flex-wrap justify-end gap-2 px-6 py-4 border-t bg-gray-50 rounded-b-xl">
          <button
            type="button"
            onClick={handleExportPdf}
            disabled={exportDisabled}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm rounded-lg border border-gray-300 bg-white hover:bg-gray-50 disabled:opacity-50"
          >
            <Download size={16} />
            {isExportingPdf ? 'Export…' : 'Exporter PDF'}
          </button>
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm rounded-lg border border-gray-300 bg-white hover:bg-gray-50"
          >
            Fermer
          </button>
          <Can permission={PERMISSIONS.KYC_VERIFY}>
            {user.kycStatus === 'PENDING' && onRejectKYC && onVerifyKYC && (
              <>
                <button
                  type="button"
                  disabled={isProcessing}
                  onClick={() => onRejectKYC(user._id)}
                  className="px-4 py-2 text-sm rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
                >
                  Rejeter KYC
                </button>
                <button
                  type="button"
                  disabled={isProcessing}
                  onClick={() => onVerifyKYC(user._id)}
                  className="px-4 py-2 text-sm rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
                >
                  Valider KYC
                </button>
              </>
            )}
          </Can>
        </div>
      </div>
    </div>
  );
};

export default UserDetailsModal;
